import { z } from 'zod';
import { InteractionProvider } from '../index.js';
import { InteractionEventType } from './index.js';
// --- Shared Sub-schemas ---
const SparkPostBaseEventSchema = z.object({
    provider: z.literal(InteractionProvider.SparkPost),
});
const GeoIpSchema = z.object({
    zip: z.number(),
    city: z.string(),
    region: z.string(),
    country: z.string(),
    continent: z.string(),
    latitude: z.number().or(z.string()),
    longitude: z.number().or(z.string()),
    postal_code: z.string(),
});
const RcptMetaSchema = z.object({
    tenantId: z.uuid(),
    interactionId: z.uuid(),
});
const UserAgentParsedSchema = z.object({
    is_proxy: z.boolean(),
    is_mobile: z.boolean(),
    os_family: z.string(),
    os_version: z.string(),
    agent_family: z.string(),
    device_brand: z.string(),
    device_family: z.string(),
    is_prefetched: z.boolean(),
});
// --- Base Data Schema (Fields present in BOTH events) ---
const BaseBronzeEventData = z.object({
    ip_pool: z.string(),
    rcpt_to: z.string(),
    subject: z.string().optional(),
    event_id: z.string(),
    msg_from: z.string(),
    msg_size: z.string(),
    rcpt_meta: RcptMetaSchema,
    rcpt_tags: z.array(z.string()),
    timestamp: z.string(),
    message_id: z.string(),
    sending_ip: z.string(),
    customer_id: z.string(),
    raw_rcpt_to: z.string(),
    template_id: z.string(),
    friendly_from: z.string(),
    subaccount_id: z.string(),
    transactional: z.string(),
    injection_time: z.string(),
    routing_domain: z.string(),
    transmission_id: z.string(),
    mailbox_provider: z.string(),
    recipient_domain: z.string(),
    template_version: z.string(),
    mailbox_provider_region: z.string(),
});
// Bounced
const SparkPostBounceEventBronzeSchema = BaseBronzeEventData.extend({
    type: z.literal('bounce'),
    reason: z.string(),
    error_code: z.string(),
    raw_reason: z.string(),
    bounce_class: z.string(),
    num_retries: z.string(),
    recv_method: z.string(),
    ip_address: z.string().optional(),
    initial_pixel: z.boolean(),
    open_tracking: z.boolean(),
});
export const SparkPostBounceEventSchema = SparkPostBaseEventSchema.extend({
    bronzeData: SparkPostBounceEventBronzeSchema,
});
// Out of Band
const SparkPostOutOfBandEventBronzeSchema = BaseBronzeEventData.extend({
    type: z.literal('out_of_band'),
    reason: z.string(),
    error_code: z.string(),
    raw_reason: z.string(),
    bounce_class: z.string(),
    num_retries: z.string().optional(),
    recv_method: z.string(),
    delv_method: z.string(),
});
export const SparkPostOutOfBandEventSchema = SparkPostBaseEventSchema.extend({
    bronzeData: SparkPostOutOfBandEventBronzeSchema,
});
// The "Delivered" Event Data
const SparkPostDeliveredEventBronzeSchema = BaseBronzeEventData.extend({
    type: z.literal('delivery'), // Discriminator
    queue_time: z.string(),
    num_retries: z.string(),
    recv_method: z.string(),
    outbound_tls: z.string(),
    ip_address: z.string().optional(),
    delv_method: z.string(),
    initial_pixel: z.boolean(),
    open_tracking: z.boolean(),
});
export const SparkPostDeliveredEventSchema = SparkPostBaseEventSchema.extend({
    bronzeData: SparkPostDeliveredEventBronzeSchema,
});
// The "Open" Event Data
const SparkPostOpenEventBronzeSchema = BaseBronzeEventData.extend({
    type: z.literal('open'), // Discriminator
    geo_ip: GeoIpSchema.optional(),
    user_agent: z.string(),
    user_agent_parsed: UserAgentParsedSchema,
    ip_address: z.string().optional(),
    initial_pixel: z.boolean(),
    open_tracking: z.boolean(),
});
export const SparkPostOpenEventSchema = SparkPostBaseEventSchema.extend({
    bronzeData: SparkPostOpenEventBronzeSchema,
});
// The "Complained" Event Data
const SparkPostComplainedEventBronzeSchema = BaseBronzeEventData.extend({
    type: z.literal('spam_complaint'), // Discriminator
    delv_method: z.string(),
    fbtype: z.string(),
});
export const SparkPostComplainedEventSchema = SparkPostBaseEventSchema.extend({
    bronzeData: SparkPostComplainedEventBronzeSchema,
});
// The "Initial Open" Event Data
const SparkPostInitialOpenEventBronzeSchema = BaseBronzeEventData.extend({
    type: z.literal('initial_open'), // Discriminator
    geo_ip: GeoIpSchema.optional(),
    user_agent: z.string(),
    user_agent_parsed: UserAgentParsedSchema,
    ip_address: z.string().optional(),
    initial_pixel: z.boolean(),
    open_tracking: z.boolean(),
});
export const SparkPostInitialOpenEventSchema = SparkPostBaseEventSchema.extend({
    bronzeData: SparkPostInitialOpenEventBronzeSchema,
});
// The "Clicked" Event Data
const SparkPostClickedEventBronzeSchema = BaseBronzeEventData.extend({
    type: z.literal('click'), // Discriminator
    geo_ip: GeoIpSchema,
    user_agent: z.string(),
    user_agent_parsed: UserAgentParsedSchema,
    ip_address: z.string(),
    initial_pixel: z.boolean(),
    open_tracking: z.boolean(),
});
export const SparkPostClickedEventSchema = SparkPostBaseEventSchema.extend({
    bronzeData: SparkPostClickedEventBronzeSchema,
});
// Delay
const SparkPostDelayEventBronzeSchema = BaseBronzeEventData.extend({
    type: z.literal('delay'),
    reason: z.string(),
    error_code: z.string(),
    raw_reason: z.string(),
    bounce_class: z.string(),
    queue_time: z.string(),
    num_retries: z.string(),
    recv_method: z.string(),
    initial_pixel: z.boolean(),
    open_tracking: z.boolean(),
});
export const SparkPostDelayEventSchema = SparkPostBaseEventSchema.extend({
    bronzeData: SparkPostDelayEventBronzeSchema,
});
// Internal Error
const SparkPostInternalErrorEventBronzeSchema = z.string();
export const SparkPostInternalErrorEventSchema = SparkPostBaseEventSchema.extend({
    bronzeData: SparkPostInternalErrorEventBronzeSchema,
});
export const mapSparkPostEvent = (bronzeEvent) => {
    switch (bronzeEvent.type) {
        case 'delivery': // Recipient mailbox provider acknowledged receipt of email.
            return {
                provider: InteractionProvider.SparkPost,
                bronzeData: bronzeEvent,
                type: InteractionEventType.Delivered,
                data: {
                    recipientDomain: bronzeEvent.recipient_domain,
                    recipientEmail: bronzeEvent.rcpt_to,
                    mailboxProvider: bronzeEvent.mailbox_provider,
                    recipientIpAddress: bronzeEvent.ip_address,
                    deliveryMethod: bronzeEvent.delv_method,
                    routingDomain: bronzeEvent.routing_domain,
                },
            };
        case 'bounce': {
            return {
                provider: InteractionProvider.SparkPost,
                bronzeData: bronzeEvent,
                type: InteractionEventType.Failed,
                data: {
                    recipientEmail: bronzeEvent.rcpt_to,
                    bounceClass: bronzeEvent.bounce_class,
                    errorCode: bronzeEvent.error_code,
                    reason: bronzeEvent.reason,
                },
            };
        }
        case 'delay': // Recipient mailbox provider temporarily rejected the email.
            return {
                provider: InteractionProvider.SparkPost,
                bronzeData: bronzeEvent,
                type: InteractionEventType.Delayed,
                data: {
                    recipientEmail: bronzeEvent.rcpt_to,
                    bounceClass: bronzeEvent.bounce_class,
                    errorCode: bronzeEvent.error_code,
                    reason: bronzeEvent.reason,
                    queueTime: bronzeEvent.queue_time,
                    numRetries: bronzeEvent.num_retries,
                },
            };
        case 'out_of_band': {
            // Recipient mailbox provider rejected the email after initially accepting it.
            return {
                provider: InteractionProvider.SparkPost,
                bronzeData: bronzeEvent,
                type: InteractionEventType.Failed,
                data: {
                    recipientEmail: bronzeEvent.rcpt_to,
                    bounceClass: bronzeEvent.bounce_class,
                    errorCode: bronzeEvent.error_code,
                    reason: bronzeEvent.reason,
                },
            };
        }
        case 'initial_open': // Recipient opened the email. Recorded by a tracking pixel at the top of the email.
            return {
                provider: InteractionProvider.SparkPost,
                bronzeData: bronzeEvent,
                type: InteractionEventType.Opened,
                data: {
                    geoIp: bronzeEvent.geo_ip
                        ? {
                            zipcode: bronzeEvent.geo_ip.postal_code || undefined,
                            city: bronzeEvent.geo_ip.city || undefined,
                            country: bronzeEvent.geo_ip.country || undefined,
                            longitude: bronzeEvent.geo_ip.longitude || undefined,
                            latitude: bronzeEvent.geo_ip.latitude || undefined,
                        }
                        : undefined,
                },
            };
        case 'open': // Recipient opened the email. Recorded by a tracking pixel at the bottom of the email.
            return {
                provider: InteractionProvider.SparkPost,
                bronzeData: bronzeEvent,
                type: InteractionEventType.Opened,
                data: {
                    geoIp: bronzeEvent.geo_ip
                        ? {
                            zipcode: bronzeEvent.geo_ip.postal_code || undefined,
                            city: bronzeEvent.geo_ip.city || undefined,
                            country: bronzeEvent.geo_ip.country || undefined,
                            longitude: bronzeEvent.geo_ip.longitude || undefined,
                            latitude: bronzeEvent.geo_ip.latitude || undefined,
                        }
                        : undefined,
                },
            };
        case 'click': // Recipient opened the email. Recorded by a tracking pixel at the bottom of the email.
            return {
                provider: InteractionProvider.SparkPost,
                bronzeData: bronzeEvent,
                type: InteractionEventType.Clicked,
                data: {
                    geoIp: bronzeEvent.geo_ip
                        ? {
                            zipcode: bronzeEvent.geo_ip.postal_code || undefined,
                            city: bronzeEvent.geo_ip.city || undefined,
                            country: bronzeEvent.geo_ip.country || undefined,
                            longitude: bronzeEvent.geo_ip.longitude || undefined,
                            latitude: bronzeEvent.geo_ip.latitude || undefined,
                        }
                        : undefined,
                },
            };
        case 'spam_complaint': // Recipient marked the email as spam.
            return {
                provider: InteractionProvider.SparkPost,
                bronzeData: bronzeEvent,
                type: InteractionEventType.Complained,
                data: {},
            };
        case 'injection': // Email successfully generated or relayed to SparkPost.
        case 'policy_rejection': // SparkPost rejected the email due to a policy reason.
        case 'generation_failure': // Email generation failed due to a technical reason.
        case 'generation_rejection': // Email generation failed due to a policy reason.
        case 'list_unsubscribe': // Recipient unsubscribed using a mailbox provider's list unsubscribe feature.
        case 'link_unsubscribe': // User clicked a tagged unsubscribe link.
        case 'relay_injection': // SparkPost received an inbound email.
        case 'relay_rejection': // SparkPost rejected an inbound email.
        case 'relay_delivery': // Delivered inbound email to your endpoint.
        case 'relay_tempfail': // Your endpoint temporarily failed to accept inbound email.
        case 'relay_permfail': // Reached the maximum retry delivery attempts for an inbound email.
            return null;
        default:
            return null;
    }
};
//# sourceMappingURL=spark-post.js.map